import React, {useEffect} from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {useSearchParams} from "react-router-dom";
import {useLocale, useMessages} from "i18n";
import {useHasMounted} from "utils/useHasMounted";
import {purchaseAlreadyReported, rememberSettlingPurchase} from "analytics/purchaseConversions";
import BaseInput from "components/BaseInput";
import {EmailSuggestionChip} from "components/emailSuggestion/EmailSuggestionChip";
import {ContentSurface} from "components/ContentPage";
import {DisplayAccent, DisplaySerif} from "theme/typography";
import {
	categoryGroups,
	categoryOf,
	priceMinor,
	SPONSOR_LIMITS,
	BOOKABLE_TEMPLATES,
} from "./sponsorBooking";
import {isVideoTemplate} from "./sponsorOptions";
import {sponsorOptionSummary, type SponsorOptionSummaryWords} from "./optionSummary";
import {SponsorOptionsPanel} from "./options/SponsorOptionsPanel";
import {VideoCreativeFields} from "./options/VideoCreativeFields";
import {WallpaperPreview} from "./options/WallpaperPreview";
import {rangeDays} from "./sponsorCalendar";
import {formatMinor} from "./money";
import type {SponsorErrorKey} from "./sponsorErrors";
import {useSponsorBooking, type SponsorField} from "./useSponsorBooking";
import {AdvertisingTermsLink} from "./AdvertisingTermsLink";
import {SPONSOR_TERMS_CONSENT_TESTID} from "./sponsorTermsConsent";
import {SponsorCalendar} from "./calendar/SponsorCalendar";
import {SponsorMyBookings} from "./SponsorMyBookings";
import {BackdropTemplatePicker} from "./BackdropTemplatePicker";
import type {PreviewVideoArt} from "./takeover/backdropArt";
import {
	AddAssetLabel,
	AssetControls,
	CategoryGroup,
	CategoryHead,
	CategoryLede,
	CategoryName,
	OptionsPanel,
	OptionsPreview,
	AssetCounter,
	AssetImage,
	AssetRow,
	AssetTile,
	EmptyNote,
	Field,
	FieldError,
	FieldHint,
	Fields,
	Hero,
	HeroLede,
	HiddenFileInput,
	LoadingPanel,
	Note,
	Panel,
	PerDayNote,
	RemoveButton,
	RequestError,
	Section,
	SectionHead,
	SectionLede,
	SectionTitle,
	StepLabel,
	StepNumber,
	SubmitButton,
	SubmitRow,
	SummaryList,
	SummaryTerm,
	SummaryValue,
	TermsConsentBox,
	TermsConsentRow,
	TermsConsentText,
	TileButton,
	TotalAmount,
	TotalLabel,
	TotalRow,
	WideField,
} from "./SponsorPage.styles";
import {PickedImageOutcome} from "components/PickedImageOutcome";

/**
 * The public self-serve sponsorship page at `/sponsor` (ui#496).
 *
 * A dumb view over {@link useSponsorBooking} (epic #26): it renders what the hook holds and reports
 * events back. Nothing here decides what is valid, what a click does to a date range, or what a
 * rejection means — those live in `sponsorBooking.ts`, `sponsorCalendar.ts` and `sponsorErrors.ts`,
 * none of which import React.
 *
 * The page stays PUBLIC after epic #441: the calendar and the per-day rate are anonymous reads, so a
 * stranger can price a takeover before deciding whether to open an account. Only the two writes need
 * one, and {@link useSponsorBooking} routes an anonymous visitor to sign-in rather than to a 401.
 * {@link SponsorMyBookings} below renders the signed-in advertiser's own bookings and nothing at all for
 * everybody else — both branches are login-state dependent and therefore both must be hydration-safe.
 *
 * SSG-safe: no `window`/`document` at module scope, and the one thing that legitimately differs
 * between the pre-rendered HTML and the live page. The `?booking=` deep link. Is gated behind
 * {@link useHasMounted} here and in {@link SponsorMyBookings}, so the first client render still matches
 * the static markup (ui#74). The login-dependent branches ride on `useIsLoggedIn`, which carries the
 * same guarantee.
 */

/** Stable ids so each field can point at its own hint and error through `aria-describedby`. */
const ID = {
	name: "sponsor-name",
	email: "sponsor-email",
	emailHint: "sponsor-email-hint",
	headline: "sponsor-headline",
	tagline: "sponsor-tagline",
	ctaLabel: "sponsor-cta-label",
	ctaUrl: "sponsor-cta-url",
	earlyStart: "sponsor-early-start",
	terms: "sponsor-terms",
} as const;

/** `aria-describedby` takes a space-separated id list; falsy entries are dropped. */
function describedBy(...ids: (string | false | null | undefined)[]): string | undefined {
	const present = ids.filter((id): id is string => Boolean(id));
	return present.length > 0 ? present.join(" ") : undefined;
}

/**
 * The form name every treatment radio shares.
 *
 * ONE name across both category groups on purpose: same-name radios are a single exclusive group to
 * the browser however they are nested, which is what keeps arrow-key navigation running through the
 * whole catalogue and makes choosing a wallpaper clear the backdrop.
 */
const TEMPLATE_RADIO_NAME = "sponsor-template";

export function SponsorPage() {
	const m = useMessages();
	const copy = m.sponsor;
	const {locale} = useLocale();
	const booking = useSponsorBooking();
	const [searchParams, setSearchParams] = useSearchParams();
	const submittedBookingId = booking.submittedBookingId;
	// The `?booking=` return exists only on the live page; the pre-rendered artifact has no query
	// string. Reading it before mount would make the first client render diverge from the HTML.
	const hasMounted = useHasMounted();
	const paidBookingId = hasMounted ? searchParams.get("booking") : null;

	/*
	 * The advertiser may have just paid (api#1241). Only the FACT is recorded here: the browser cannot
	 * know which conversion id the server reported this booking under until it asks, and the asking, the
	 * consent gate and the event itself all belong to the shell's `usePurchaseConversions`.
	 *
	 * Armed on the PARAMETER rather than on the booking's state, and that is deliberate. Since
	 * kitecorp/transferify-api-service#1362 the same address is also where the approval email lands, and
	 * gating on "the booking reads as paid" would have been the tidier-looking rule, but the payment
	 * webhook can land seconds AFTER Stripe returns the browser, and the booking list is read once, and
	 * it is not read at all for a signed-out visitor. Every one of those would silently lose a real
	 * conversion. The cost of the looser rule is bounded and harmless: an email reader arms a poll that
	 * finds nothing, because the SERVER decides what was purchased and only ever answers with purchases
	 * it recorded.
	 *
	 * Skipped in two cases. Once this browser has already reported that booking, because this address is
	 * bookmarkable and its `?booking=` is not validated against anything: without the check, every later
	 * load of it would arm another round of authenticated reads for a purchase already told. The booking
	 * id IS the sponsor surface's conversion id, so the comparison is exact.
	 *
	 * And when THIS PAGE wrote the parameter itself, one effect below, to point the confirmation at the
	 * booking just submitted. Nothing was bought in that turn: a submission takes no money at all now,
	 * so arming there would spend fifteen minutes of authenticated polling, ten reads per navigation,
	 * looking for a purchase that cannot exist. It costs nothing on the journeys that matter, because a
	 * real payment returns from Stripe as a fresh page load, where no submission has happened and
	 * `submittedBookingId` is null.
	 */
	const selfWrittenBookingId = submittedBookingId;
	useEffect(() => {
		if (paidBookingId === null || paidBookingId === selfWrittenBookingId) {
			return;
		}
		if (!purchaseAlreadyReported(paidBookingId)) {
			rememberSettlingPurchase();
		}
	}, [paidBookingId, selfWrittenBookingId]);

	/*
	 * Put the accepted booking's id in the address as soon as it exists.
	 *
	 * That is the same `?booking=` the approval email will carry, so the confirmation below highlights
	 * and scrolls to the new booking through the mechanism that already exists, instead of a second
	 * "which row is new" rule, and the resulting address is one the advertiser can bookmark or send to
	 * a colleague and land back on this booking.
	 */
	useEffect(() => {
		if (submittedBookingId !== null) {
			setSearchParams({booking: submittedBookingId}, {replace: true});
		}
	}, [submittedBookingId]);
	const errorText = (field: SponsorField): string | null => {
		const key = booking.errors[field];
		return key === undefined ? null : copy.errors[key as SponsorErrorKey];
	};

	// The picker shuts while a batch is in flight and once the chosen treatment is full. Read once so the
	// label's affordance and the input's `disabled` cannot disagree about which of those is true.
	const pickerClosed = booking.uploading || booking.assets.length >= booking.assetCapacity;

	const wallpaper = categoryOf(booking.draft.template) === "WALLPAPER";
	/*
	 * Whether step 2 asks for a video or for pictures (kitecorp/transferify-ui#1867).
	 *
	 * Read from the shared table rather than compared against the template name, so the page cannot
	 * disagree with the validator about which treatment takes which creative.
	 */
	const video = isVideoTemplate(booking.draft.template);
	/** What the picker tiles and the live preview draw for the video treatment, as it is uploaded. */
	const videoArt: PreviewVideoArt = {
		videoUrl: booking.video?.url ?? null,
		posterUrl: booking.poster?.url ?? null,
	};

	/**
	 * The wallpaper settings as the review step lists them: a term and a translated WORD.
	 *
	 * The SETTINGS come from `sponsor/optionSummary.ts` since kitecorp/transferify-ui#1870 — the same
	 * module the operator queue reads — so the words an advertiser confirms and the words a reviewer
	 * approves are one list rather than two that drift. What was here before was that list, inline, and
	 * rebuilding it for the queue would have made the operator's copy the second one.
	 *
	 * The uploaded FILES stay here, and deliberately so: this surface lists them as words because an
	 * advertiser is confirming what they attached, while the queue renders the files themselves. A
	 * shared function for that would have been generality nothing asked for.
	 */
	const summaryWords: SponsorOptionSummaryWords = {terms: copy.summary, values: copy.options};
	const optionSummary: readonly {key: string; term: string; value: string}[] = [
		...sponsorOptionSummary(booking.draft.template, booking.options, summaryWords),
		...(!wallpaper ? [] : [
			{key: "logo",
				term: copy.summary.logo,
				value: booking.logo === null ? copy.summary.unset : copy.summary.added},
			// The video creative, on the one treatment that has it. Listed for the same reason every
			// option above is: the review names what was chosen, and a missing row reads as an omission.
			...(video ? [
				{key: "video",
					term: copy.summary.video,
					value: booking.video === null ? copy.summary.unset : copy.summary.added},
				{key: "poster",
					term: copy.summary.poster,
					value: booking.poster === null ? copy.summary.unset : copy.summary.added},
			] : []),
		]),
	];

	const days = rangeDays(booking.selection);
	const rate = booking.availability?.dailyRateMinor ?? 0;
	const currency = booking.availability?.currency ?? "";
	const totalMinor = booking.selection === null
		? 0
		: priceMinor(booking.selection.startsOn, booking.selection.endsOn, rate);

	/*
	 * Submitted, and nothing was charged (kitecorp/transferify-api-service#1362).
	 *
	 * Rendered from the hook's own state rather than from a query parameter, which is what makes it an
	 * honest confirmation: it appears because THIS press was accepted, and it cannot be reached by
	 * bookmarking an address. `?booking=` is now handled one level down, by `SponsorMyBookings`, where
	 * the booking it names can be shown with the state the server actually holds.
	 */
	if (submittedBookingId !== null) {
		return (
			<ContentSurface $wide>
				<Hero>
					<StepLabel>{copy.hero.eyebrow}</StepLabel>
					<DisplaySerif>{copy.status.submittedTitle}</DisplaySerif>
					<HeroLede>{copy.status.submittedBody}</HeroLede>
				</Hero>
				{/* The booking list IS the destination, so the panel carries no call to action of its own:
				    the new booking is in it, under review, and it is where the payment button will appear
				    once an operator approves. The `key` makes the remount explicit rather than a
				    consequence of where this element happens to sit in the other branch. The list was
				    loaded before this booking existed and has to be read again. */}
				<SponsorMyBookings key="submitted"/>
			</ContentSurface>
		);
	}

	return (
		<ContentSurface $wide>
			<Hero>
				<StepLabel>{copy.hero.eyebrow}</StepLabel>
				<DisplaySerif>
					{copy.hero.titleLead} <DisplayAccent>{copy.hero.titleAccent}</DisplayAccent>
				</DisplaySerif>
				<HeroLede>{copy.hero.lede}</HeroLede>
			</Hero>

			<form
				onSubmit={event => {
					event.preventDefault();
					// The pressed Continue, so a sign-in this press opens can hand focus back to it (ui#3117).
					void booking.submit(event.submitter ?? undefined);
				}}
			>
				<Section>
					<SectionHead>
						<StepLabel><StepNumber>1</StepNumber>{copy.steps.template}</StepLabel>
					</SectionHead>
					{categoryGroups(BOOKABLE_TEMPLATES).map(({category, templates}) => (
						<CategoryGroup key={category}>
							<CategoryHead>
								<CategoryName>{copy.categories[category].name}</CategoryName>
								<CategoryLede>{copy.categories[category].lede}</CategoryLede>
							</CategoryHead>
							<BackdropTemplatePicker
								value={booking.draft.template}
								onChange={booking.setTemplate}
								templates={templates}
								assets={booking.assets}
								video={videoArt}
								groupLabel={copy.categories[category].name}
								/* One name for BOTH groups: same-name radios are a single exclusive group to
								   the browser, so a sponsor arrows straight from the last backdrop into the
								   first wallpaper and can only ever hold one of the ten. */
								name={TEMPLATE_RADIO_NAME}
							/>
						</CategoryGroup>
					))}

					{/* The poster settings belong to the treatment that has them, so they open here rather
					    than as a step of their own: the numbered 1-to-5 narrative is what a first-time buyer
					    came for, and nothing above or below it is renumbered. */}
					{wallpaper && (
						<OptionsPanel>
							<OptionsPreview>
								<WallpaperPreview
									template={booking.draft.template}
									options={booking.options}
									imageUrls={booking.assets.map(asset => asset.url)}
									videoUrl={videoArt.videoUrl}
									posterUrl={videoArt.posterUrl}
									logoUrl={booking.logo?.url ?? null}
									sponsorName={booking.draft.sponsorName}
									/* The placeholders stand in until the advertiser has written their own copy, so
									   the preview is a poster from the first paint rather than an empty block. */
									headline={booking.draft.headline || copy.form.headlinePlaceholder}
									tagline={booking.draft.tagline}
									ctaLabel={booking.draft.ctaLabel || copy.form.ctaLabelPlaceholder}
									ctaUrl={booking.draft.ctaUrl || copy.form.ctaUrlPlaceholder}
									/* The phone stage beside the desktop one: an advertiser choosing a focal point
									   has to see what the crop costs on the screen that costs the most
									   (kitecorp/transferify-ui#2143). */
									phone
								/>
							</OptionsPreview>
							<SponsorOptionsPanel
								template={booking.draft.template}
								options={booking.options}
								setOption={booking.setOption}
								copy={copy.options}
								namePrefix="sponsor-option"
								logo={{
									copy: copy.options.logo,
									asset: booking.logo,
									uploading: booking.uploading,
									onPick: (file, opener) => void booking.onPickAsset("logo", file, opener),
									onRemove: () => booking.onRemoveSingleAsset("logo"),
								}}
								/*
								 * The crop, over whichever still this treatment has (kitecorp/transferify-ui#2143).
								 * A video's POSTER FRAME rather than the clip: a marker dragged over a playing
								 * video is aimed at whatever frame happened to be on screen, and the poster is
								 * both the frame the advertiser chose to stand for it and what a reduced-motion
								 * recipient actually sees.
								 */
								focalPoint={{
									copy: copy.options.focalPoint,
									mediaUrl: (video ? videoArt.posterUrl : booking.assets[0]?.url) ?? null,
								}}
								errorText={errorText}
								onSublineBlur={() => booking.touchField("subline")}
							/>
						</OptionsPanel>
					)}
				</Section>

				<Section>
					<SectionHead>
						<StepLabel><StepNumber>2</StepNumber>{copy.steps.creative}</StepLabel>
						<SectionTitle>{copy.creative.title}</SectionTitle>
						{/* One treatment's creative is not a set of pictures, so it is not described as one. */}
						{/* Three ledes, because the shape RULE differs: a wallpaper's picture is the whole
						    page and every window crops it, while a backdrop composes inside a frame beside
						    the card and merely reads better landscape (kitecorp/transferify-ui#2143). */}
						<SectionLede>
							{video
								? copy.creative.videoLede
								: (wallpaper ? copy.creative.wallpaperLede : copy.creative.lede)}
						</SectionLede>
					</SectionHead>
					{/*
					  * The two pickers REPLACE the image set rather than joining it. A VIDEO_LOOP's arity is
					  * zero on both sides of the wire, so an image control here would offer an upload the
					  * submission then drops (`sponsorMediaForWire`) and the api refuses.
					  */}
					{video ? (
						<Panel>
							<VideoCreativeFields
								video={booking.video}
								poster={booking.poster}
								onPickVideo={(file, opener) => void booking.onPickAsset("video", file, opener)}
								onRemoveVideo={() => booking.onRemoveSingleAsset("video")}
								onPickPoster={(file, opener) => void booking.onPickAsset("poster", file, opener)}
								onRemovePoster={() => booking.onRemoveSingleAsset("poster")}
								uploading={booking.uploading}
								errorText={errorText}
							/>
						</Panel>
					) : (
						<Panel>
							{booking.assets.length === 0 && <EmptyNote>{copy.creative.empty}</EmptyNote>}
							<AssetRow>
								{booking.assets.map((asset, index) => (
									<AssetTile key={asset.assetId}>
										{/* Decorative here: the sponsor is looking at their own upload, and every
										    control beside it is individually labelled by position. */}
										<AssetImage src={asset.url} alt=""/>
										<AssetControls>
											<TileButton
												aria-label={copy.creative.moveLeftAria(index + 1)}
												title={copy.creative.moveLeft}
												disabled={index === 0}
												onClick={() => booking.onMoveAsset(index, -1)}
											>
												‹
											</TileButton>
											<RemoveButton
												aria-label={copy.creative.removeAria(index + 1)}
												title={copy.creative.remove}
												onClick={() => booking.onRemoveAsset(index)}
											>
												✕
											</RemoveButton>
											<TileButton
												aria-label={copy.creative.moveRightAria(index + 1)}
												title={copy.creative.moveRight}
												disabled={index === booking.assets.length - 1}
												onClick={() => booking.onMoveAsset(index, 1)}
											>
												›
											</TileButton>
										</AssetControls>
									</AssetTile>
								))}

								<AddAssetLabel $disabled={pickerClosed}>
									<HiddenFileInput
										type="file"
										// One dialog fills the whole treatment (ui#664). The hook admits as many as
										// there are free slots and says what it did with the rest.
										multiple
										// The api derives the stored type from magic bytes, so this is a courtesy
										// filter in the picker, never the check that matters.
										accept="image/png,image/jpeg,image/webp"
										aria-label={copy.creative.addAria}
										disabled={pickerClosed}
										aria-busy={booking.uploading}
										onChange={event => {
											const files = Array.from(event.target.files ?? []);
											// Reset first: picking the SAME file twice fires no change event
											// otherwise, so a failed upload could not be retried.
											event.target.value = "";
											if (files.length > 0) {
												void booking.onUploadFiles(files, event.currentTarget);
											}
										}}
									/>
									{booking.uploading ? copy.creative.uploading : copy.creative.add}
								</AddAssetLabel>
							</AssetRow>
							<AssetCounter>
								{booking.assets.length > booking.assetCapacity
									? copy.creative.counterOver(booking.assets.length, booking.assetCapacity)
									: copy.creative.counter(booking.assets.length, booking.assetCapacity)}
							</AssetCounter>
							{booking.uploadReport !== null && (
								/* Keyed by report so a repeated pick REMOUNTS the alert — see
								   {@link PickedImageOutcome}, which owns the live region and says why the key is
								   the caller's job. The lines are this page's own copy. */
								<PickedImageOutcome key={booking.uploadReport.reportId} title={copy.creative.problems}>
									{booking.uploadReport.refused > 0 && (
										<li>{copy.creative.overflow(booking.uploadReport.refused, booking.assetCapacity)}</li>
									)}
									{booking.uploadReport.failures.map((failure, index) => (
										<li key={`${index}-${failure.name}`}>
											{copy.creative.failedItem(failure.name, copy.errors[failure.error])}
										</li>
									))}
								</PickedImageOutcome>
							)}
							{errorText("assetIds") && (
								<FieldError role="alert">{errorText("assetIds")}</FieldError>
							)}
						</Panel>
					)}
				</Section>

				<Section>
					<SectionHead>
						<StepLabel><StepNumber>3</StepNumber>{copy.steps.dates}</StepLabel>
						<SectionTitle>{copy.calendar.title}</SectionTitle>
						<SectionLede>{copy.calendar.lede}</SectionLede>
					</SectionHead>
					{booking.loading && <LoadingPanel>{m.common.loading}</LoadingPanel>}
					{booking.availability !== null && booking.month !== null && (
						<SponsorCalendar
							availability={booking.availability}
							month={booking.month}
							selection={booking.selection}
							onMonthChange={booking.setMonth}
							onDayClick={booking.onDayClick}
						/>
					)}
					{errorText("startsOn") && <FieldError role="alert">{errorText("startsOn")}</FieldError>}
				</Section>

				<Section>
					<SectionHead>
						<StepLabel><StepNumber>4</StepNumber>{copy.steps.details}</StepLabel>
						<SectionTitle>{copy.form.title}</SectionTitle>
						<SectionLede>{copy.form.lede}</SectionLede>
					</SectionHead>
					<Panel>
						<Fields>
							<Field>
								<BaseInput
									id={ID.name}
									variant="outlined"
									label={copy.form.sponsorName}
									placeholder={copy.form.sponsorNamePlaceholder}
									value={booking.draft.sponsorName}
									error={errorText("sponsorName") !== null}
									inputProps={{maxLength: SPONSOR_LIMITS.sponsorName}}
									aria-describedby={describedBy(errorText("sponsorName") && `${ID.name}-error`)}
									onBlur={() => booking.touchField("sponsorName")}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										booking.setField("sponsorName", event.target.value)}
								/>
								{errorText("sponsorName") && (
									<FieldError id={`${ID.name}-error`} role="alert">{errorText("sponsorName")}</FieldError>
								)}
							</Field>

							<Field>
								<BaseInput
									id={ID.email}
									type="email"
									variant="outlined"
									label={copy.form.sponsorEmail}
									placeholder={copy.form.sponsorEmailPlaceholder}
									value={booking.draft.sponsorEmail}
									error={errorText("sponsorEmail") !== null}
									inputProps={{maxLength: SPONSOR_LIMITS.sponsorEmail}}
									aria-describedby={describedBy(
										ID.emailHint,
										errorText("sponsorEmail") && `${ID.email}-error`,
									)}
									onBlur={() => booking.touchField("sponsorEmail")}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										booking.setField("sponsorEmail", event.target.value)}
								/>
								{/* ui#1959 — since sponsor bookings pay AFTER approval, the approval mail is the
								  * only way a sponsor learns their slot is theirs. */}
								<EmailSuggestionChip
									value={booking.draft.sponsorEmail}
									onAccept={value => booking.setField("sponsorEmail", value)}
								/>
								<FieldHint id={ID.emailHint}>{copy.form.sponsorEmailHint}</FieldHint>
								{errorText("sponsorEmail") && (
									<FieldError id={`${ID.email}-error`} role="alert">{errorText("sponsorEmail")}</FieldError>
								)}
							</Field>

							<WideField>
								<BaseInput
									id={ID.headline}
									variant="outlined"
									label={copy.form.headline}
									placeholder={copy.form.headlinePlaceholder}
									value={booking.draft.headline}
									error={errorText("headline") !== null}
									inputProps={{maxLength: SPONSOR_LIMITS.headline}}
									aria-describedby={describedBy(errorText("headline") && `${ID.headline}-error`)}
									onBlur={() => booking.touchField("headline")}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										booking.setField("headline", event.target.value)}
								/>
								{errorText("headline") && (
									<FieldError id={`${ID.headline}-error`} role="alert">{errorText("headline")}</FieldError>
								)}
							</WideField>

							<WideField>
								<BaseInput
									id={ID.tagline}
									variant="outlined"
									label={`${copy.form.tagline} (${copy.form.optional})`}
									placeholder={copy.form.taglinePlaceholder}
									value={booking.draft.tagline}
									error={errorText("tagline") !== null}
									inputProps={{maxLength: SPONSOR_LIMITS.tagline}}
									aria-describedby={describedBy(errorText("tagline") && `${ID.tagline}-error`)}
									onBlur={() => booking.touchField("tagline")}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										booking.setField("tagline", event.target.value)}
								/>
								{errorText("tagline") && (
									<FieldError id={`${ID.tagline}-error`} role="alert">{errorText("tagline")}</FieldError>
								)}
							</WideField>

							<Field>
								<BaseInput
									id={ID.ctaLabel}
									variant="outlined"
									label={copy.form.ctaLabel}
									placeholder={copy.form.ctaLabelPlaceholder}
									value={booking.draft.ctaLabel}
									error={errorText("ctaLabel") !== null}
									inputProps={{maxLength: SPONSOR_LIMITS.ctaLabel}}
									aria-describedby={describedBy(errorText("ctaLabel") && `${ID.ctaLabel}-error`)}
									onBlur={() => booking.touchField("ctaLabel")}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										booking.setField("ctaLabel", event.target.value)}
								/>
								{errorText("ctaLabel") && (
									<FieldError id={`${ID.ctaLabel}-error`} role="alert">{errorText("ctaLabel")}</FieldError>
								)}
							</Field>

							<Field>
								<BaseInput
									id={ID.ctaUrl}
									type="url"
									variant="outlined"
									label={copy.form.ctaUrl}
									placeholder={copy.form.ctaUrlPlaceholder}
									value={booking.draft.ctaUrl}
									error={errorText("ctaUrl") !== null}
									inputProps={{maxLength: SPONSOR_LIMITS.ctaUrl}}
									aria-describedby={describedBy(errorText("ctaUrl") && `${ID.ctaUrl}-error`)}
									onBlur={() => booking.touchField("ctaUrl")}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
										booking.setField("ctaUrl", event.target.value)}
								/>
								{errorText("ctaUrl") && (
									<FieldError id={`${ID.ctaUrl}-error`} role="alert">{errorText("ctaUrl")}</FieldError>
								)}
							</Field>
						</Fields>
					</Panel>
				</Section>

				<Section>
					<SectionHead>
						<StepLabel><StepNumber>5</StepNumber>{copy.steps.review}</StepLabel>
						<SectionTitle>{copy.summary.title}</SectionTitle>
					</SectionHead>
					<Panel>
						<SummaryList>
							<SummaryTerm>{copy.summary.template}</SummaryTerm>
							<SummaryValue>{copy.templates[booking.draft.template].name}</SummaryValue>

							<SummaryTerm>{copy.summary.category}</SummaryTerm>
							<SummaryValue>
								{copy.categories[categoryOf(booking.draft.template)].name}
							</SummaryValue>

							{/* The wallpaper settings, IN WORDS. The enum names are what travels; a review step
							    that showed RIGHT_LEFT would be asking the advertiser to confirm a wire value. */}
							{optionSummary.map(row => (
								<React.Fragment key={row.key}>
									<SummaryTerm>{row.term}</SummaryTerm>
									<SummaryValue>{row.value}</SummaryValue>
								</React.Fragment>
							))}

							<SummaryTerm>{copy.summary.dates}</SummaryTerm>
							<SummaryValue>
								{booking.selection === null
									? copy.calendar.nothingSelected
									: `${booking.selection.startsOn} — ${booking.selection.endsOn}`}
							</SummaryValue>

							<SummaryTerm>{copy.summary.length}</SummaryTerm>
							<SummaryValue>{copy.calendar.selection(days)}</SummaryValue>
						</SummaryList>

						<TotalRow>
							<TotalLabel>
								{copy.summary.total}
								{booking.availability !== null && (
									<PerDayNote>{copy.summary.perDay(formatMinor(rate, currency, locale))}</PerDayNote>
								)}
							</TotalLabel>
							<TotalAmount>
								{booking.availability === null ? "—" : formatMinor(totalMinor, currency, locale)}
							</TotalAmount>
						</TotalRow>

						<Note>{copy.summary.vatNote}</Note>
						<Note>{copy.summary.reviewNote}</Note>
						<FormControlLabel control={<Checkbox checked={booking.draft.consumerBooking === true}
							onChange={(_, checked) => booking.setField("consumerBooking", checked)}/>}
							label={copy.consumer.consumerCheckbox}/>
						<Note>{copy.consumer.businessHint}</Note>
						{booking.draft.consumerBooking && <>
							<Note>{copy.consumer.information}</Note>
							<FormControlLabel control={<Checkbox checked={booking.draft.earlyStartRequested === true}
								slotProps={{input: {"aria-describedby": describedBy(errorText("earlyStartRequested") && `${ID.earlyStart}-error`)}}}
								onChange={(_, checked) => booking.setField("earlyStartRequested", checked)}/>}
								label={copy.consumer.earlyStartCheckbox}/>
							{errorText("earlyStartRequested") && <FieldError id={`${ID.earlyStart}-error`} role="alert">{errorText("earlyStartRequested")}</FieldError>}
						</>}

						{/* The Advertising Terms, accepted HERE (kitecorp/transferify-ui#2602): the form is
						    where the advertiser reads the content rules before uploading against them, and
						    the tick is what the pay step later refers back to. A plain checkbox inside a
						    label, as the checkout consent is: the whole sentence is the hit target, and the
						    link inside it navigates without ticking the box, because a label has no
						    activation behaviour for events aimed at its interactive descendants. Unticked on
						    every fresh form, and the domain refuses the submit until it is ticked. */}
						<TermsConsentRow>
							<TermsConsentBox
								type="checkbox"
								checked={booking.draft.termsAccepted}
								onChange={event => booking.setTermsAccepted(event.target.checked)}
								data-testid={SPONSOR_TERMS_CONSENT_TESTID}
								// Same association the early-start box above carries: the refusal is announced as
								// this control's own description, so a screen-reader user who lands on the box
								// hears why the submit was refused rather than meeting an unexplained alert.
								aria-describedby={describedBy(errorText("termsAccepted") && `${ID.terms}-error`)}
								aria-invalid={errorText("termsAccepted") ? true : undefined}
							/>
							<TermsConsentText>
								{m.sponsorConsent.acceptTerms(
									<AdvertisingTermsLink document={booking.advertisingTerms}>
										{copy.summary.termsLink}
									</AdvertisingTermsLink>,
								)}
							</TermsConsentText>
						</TermsConsentRow>
						{errorText("termsAccepted") && (
							<FieldError id={`${ID.terms}-error`} role="alert">{errorText("termsAccepted")}</FieldError>
						)}

						{booking.requestError !== null && (
							<RequestError role="alert">{copy.errors[booking.requestError]}</RequestError>
						)}

						<SubmitRow>
							<SubmitButton disabled={booking.busy || booking.availability === null} aria-busy={booking.busy}>
								{booking.busy ? copy.summary.submitting : copy.summary.submit}
							</SubmitButton>
						</SubmitRow>
					</Panel>
				</Section>
			</form>

			{/* The advertiser's own bookings (epic #441), on this same page rather than a route of their
			    own — there is one place where sponsorship lives, and this is it. Rendered AFTER the form
			    and OUTSIDE it: the numbered 1→5 narrative is what a first-time buyer came for, and this
			    addition moves nothing above it. Renders nothing at all for a signed-out visitor. */}
			<SponsorMyBookings/>
		</ContentSurface>
	);
}
